import React, { useState } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { creditDistributor, isEmailUnique } from "../../features/distributor/DistributorSlice";
import { RootState } from "../../store/store";
import { ArrowLeft } from "lucide-react";
import { Loader2, AlertCircle, Plus, Trash2 } from "lucide-react";
import BuildCataloguePage from "./BuildCataloguePage";

interface AddDistributorPageProps {
  onBack: () => void;
}

interface FormData {
  currency: string;
  country: string;
  paymentType: number | null;
  name: string;
  businessName: string;
  email: string;
  password: string;
  logo?: File | null;
  hostname: string;
  isNonUniqueReferenceIdAllowed: boolean;
  whiteListedIps: string[];
  isPurchaseOrderRequired: boolean;
  allowPurchaseOrderCreation: boolean;
  contactInformation: {
    email: string;
    phone: string;
    contactPerson: string;
    address: {
      city: string;
      state: string;
      street: string;
      country: string;
      zipCode: string;
    };
  };
}

const AddDistributorPage: React.FC<AddDistributorPageProps> = ({ onBack }) => {
  const { code } = useMoney();

  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<FormData>({
    currency: "",
    country: "",
    paymentType: null,
    name: "",
    businessName: "",
    email: "",
    password: "",
    logo: null,
    hostname: "",
    isNonUniqueReferenceIdAllowed: true,
    whiteListedIps: [],
    isPurchaseOrderRequired: false,
    allowPurchaseOrderCreation: false,
    contactInformation: {
      email: "",
      phone: "",
      contactPerson: "",
      address: {
        city: "",
        state: "",
        street: "",
        country: "",
        zipCode: "",
      },
    },
  });

  const [formErrors, setFormErrors] = useState({
    currency: "",
    country: "",
    paymentType: "",
    name: "",
    businessName: "",
    email: "",
    password: "",
    hostname: "",
    contact_information: {
      email: "",
      phone: "",
      contactPerson: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
      },
    },
  });

  const [showBuildCatalogue, setShowBuildCatalogue] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [newIp, setNewIp] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const keys = name.split(".");

    console.log('handleChange called:', { name, value, type, checked: (e.target as HTMLInputElement).checked });

    if (type === "checkbox") {
      console.log('Checkbox change:', { name, checked: (e.target as HTMLInputElement).checked });
      setFormData(prevData => {
        const newData = {
          ...prevData,
          [name]: (e.target as HTMLInputElement).checked,
        };
        console.log('New form data:', newData);
        return newData;
      });
    } else if (keys.length === 2 && keys[1] === "phone") {
      const numericValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: numericValue,
        },
      });
    } else if (keys.length === 3) {
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: {
            ...((formData as any)[keys[0]][keys[1]] as object),
            [keys[2]]: value,
          },
        },
      });
    } else if (keys.length === 2) {
      setFormData({
        ...formData,
        [keys[0]]: {
          ...(formData[keys[0] as keyof typeof formData] as object),
          [keys[1]]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "paymentType" ? parseInt(value, 10) : value,
      });
    }

    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        logo: file
      });
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let hasError = false;
    const errors = {
      currency: "",
      country: "",
      paymentType: "",
      name: "",
      businessName: "",
      email: "",
      password: "",
      hostname: "",
      contact_information: {
        email: "",
        phone: "",
        contactPerson: "",
        address: {
          street: "",
          city: "",
          state: "",
          country: "",
          zipCode: "",
        },
      },
    };

    if (!formData.businessName) {
      hasError = true;
      errors.businessName = "Please enter valid business name";
    }

    if (!formData.name) {
      hasError = true;
      errors.name = "Please enter valid name";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      hasError = true;
      errors.email = "Please enter valid email";
    } else {
      const response = await dispatch(isEmailUnique({ email: formData.email }))
      if (!response.payload.isUnique) {
        hasError = true;
        errors.email = "Email already exists";
      }
    }

    if (!formData.password) {
      hasError = true;
      errors.password = "Please enter valid password";
    }

    if (!formData.country) {
      hasError = true;
      errors.country = "Please select a country";
    }

    if (!formData.currency) {
      hasError = true;
      errors.currency = "Please select a currency";
    }

    if (formData.paymentType === null) {
      hasError = true;
      errors.paymentType = "Please select payment type";
    }

    // Validate hostname (optional field, but if provided must be valid)
    if (formData.hostname && formData.hostname.trim()) {
      // Hostname should be a single word (no spaces), lowercase alphanumeric with hyphens/underscores
      const hostnameRegex = /^[a-z0-9]([a-z0-9_-]*[a-z0-9])?$/;
      if (!hostnameRegex.test(formData.hostname)) {
        hasError = true;
        errors.hostname = "Hostname must be lowercase, no spaces, and can only contain letters, numbers, hyphens, and underscores";
      } else if (formData.hostname.length > 63) {
        hasError = true;
        errors.hostname = "Hostname must be 63 characters or less";
      }
    }

    if (!formData.contactInformation.email) {
      hasError = true;
      errors.contact_information.email = "Please enter valid contact email";
    }

    if (!formData.contactInformation.phone) {
      hasError = true;
      errors.contact_information.phone = "Please enter valid contact phone";
    }

    if (!formData.contactInformation.contactPerson) {
      hasError = true;
      errors.contact_information.contactPerson = "Please enter valid contact person";
    }

    if (!formData.contactInformation.address.street) {
      hasError = true;
      errors.contact_information.address.street = "Please enter valid street";
    }

    if (!formData.contactInformation.address.city) {
      hasError = true;
      errors.contact_information.address.city = "Please enter valid city";
    }

    if (!formData.contactInformation.address.state) {
      hasError = true;
      errors.contact_information.address.state = "Please enter valid state";
    }

    if (!formData.contactInformation.address.country) {
      hasError = true;
      errors.contact_information.address.country = "Please enter valid country";
    }

    if (!formData.contactInformation.address.zipCode) {
      hasError = true;
      errors.contact_information.address.zipCode = "Please enter valid zip code";
    }

    if (hasError) {
      setFormErrors(errors);
      
      // Get the first error field and scroll to it
      const errorFields = [];
      if (errors.businessName) errorFields.push({ key: 'businessName', id: 'business_name' });
      if (errors.name) errorFields.push({ key: 'name', id: 'name' });
      if (errors.email) errorFields.push({ key: 'email', id: 'email' });
      if (errors.password) errorFields.push({ key: 'password', id: 'password' });
      if (errors.country) errorFields.push({ key: 'country', id: 'country' });
      if (errors.currency) errorFields.push({ key: 'currency', id: 'currency' });
      if (errors.paymentType) errorFields.push({ key: 'paymentType', id: 'paymentType' });
      if (errors.contact_information.email) errorFields.push({ key: 'contact_information.email', id: 'contactEmail' });
      if (errors.contact_information.phone) errorFields.push({ key: 'contact_information.phone', id: 'contactPhone' });
      if (errors.contact_information.contactPerson) errorFields.push({ key: 'contact_information.contactPerson', id: 'contactPerson' });
      if (errors.contact_information.address.street) errorFields.push({ key: 'contact_information.address.street', id: 'addressStreet' });
      if (errors.contact_information.address.city) errorFields.push({ key: 'contact_information.address.city', id: 'addressCity' });
      if (errors.contact_information.address.state) errorFields.push({ key: 'contact_information.address.state', id: 'addressState' });
      if (errors.contact_information.address.country) errorFields.push({ key: 'contact_information.address.country', id: 'addressCountry' });
      if (errors.contact_information.address.zipCode) errorFields.push({ key: 'contact_information.address.zipCode', id: 'addressZipCode' });
      if (errors.hostname) errorFields.push({ key: 'hostname', id: 'hostname' });

      if (errorFields.length > 0) {
        const firstError = errorFields[0];
        const errorElement = document.getElementById(firstError.id);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }
      
      setLoading(false);
      return;
    }

    setShowBuildCatalogue(true);
    setLoading(false);
  };

  const handleBack = () => {
    setShowBuildCatalogue(false);
  };

  const handleAddIp = () => {
    if (newIp.trim() !== "") {
      setFormData({
        ...formData,
        whiteListedIps: [...formData.whiteListedIps, newIp],
      });
      setNewIp("");
    }
  };

  const handleRemoveIp = (index: number) => {
    const newIps = formData.whiteListedIps.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      whiteListedIps: newIps,
    });
  };

  if (showBuildCatalogue) {
    return (
      <BuildCataloguePage
        {...formData}
        paymentType={formData.paymentType === null ? "" : String(formData.paymentType)}
        onBack={handleBack}
        onBackListing={onBack}
      />
    );
  }

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Add Distributor
          </h1>
          </div>
        </div>

      {/* Content Section */}
      <form onSubmit={handleContinue} className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 space-y-8">
        {/* Basic Information Card */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">
            Basic Information
          </h2>
          <div className="space-y-6">
            {/* Distributor Account Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Distributor Account Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter Short Name"
                className={`block w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.name && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.name}
                </div>
              )}
            </div>

            {/* Distributor Legal Name */}
            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
                Distributor Legal Name
              </label>
              <input
                id="business_name"
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Distributor Legal Business Name"
                className={`block w-full px-3 py-2 border ${formErrors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.businessName && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.businessName}
                </div>
              )}
              </div>

            {/* Hostname */}
            <div>
              <label htmlFor="hostname" className="block text-sm font-medium text-gray-700 mb-2">
                Hostname
              </label>
              <input
                id="hostname"
                type="text"
                name="hostname"
                value={formData.hostname}
                onChange={(e) => {
                  // Convert to lowercase and remove spaces
                  const value = e.target.value.toLowerCase().replace(/\s+/g, '');
                  setFormData({
                    ...formData,
                    hostname: value
                  });
                }}
                placeholder="Enter hostname"
                className={`block w-full px-3 py-2 border ${formErrors.hostname ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.hostname && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.hostname}
                </div>
              )}
              <p className="text-sm text-gray-500 mt-1">
                This will be used to identify the distributor in the buy portal URL (e.g., {formData.hostname || "hostname"}.wl.fincentive.co). Must be lowercase, no spaces.
              </p>
            </div>

            {/* Distributor Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distributor Logo
              </label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="w-24 h-24 object-contain border border-gray-300 rounded-lg p-2"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Recommended size: 200x200px. Max file size: 2MB
              </p>
            </div>

            {/* Country and Currency Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <div className="relative">
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`block w-full px-3 py-2 border ${formErrors.country ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                  >
                <option value="" disabled>
                  Select Country
                </option>
                <option value="IND">India</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {formErrors.country && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.country}
                  </div>
                )}
              </div>

              {/* Currency */}
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Distributor Wallet Currency
                </label>
                <div className="relative">
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className={`block w-full px-3 py-2 border ${formErrors.currency ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                  >
                <option value="" disabled>
                  Select Currency
                </option>
                <option value={code}>{code}</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {formErrors.currency && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.currency}
                  </div>
                )}
              </div>
            </div>

            {/* Portal Email and Password Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Portal Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Portal Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter Email"
                  className={`block w-full px-3 py-2 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                />
                {formErrors.email && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.email}
                  </div>
                )}
              </div>

              {/* Portal Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Portal Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter Password"
                  className={`block w-full px-3 py-2 border ${formErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                />
                {formErrors.password && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.password}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Card */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">
            Contact Information
          </h2>
          <div className="space-y-6">
            {/* Contact Email and Phone Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  id="contactEmail"
                  type="email"
                  name="contactInformation.email"
                  value={formData.contactInformation.email}
                  onChange={handleChange}
                  placeholder="Enter Contact Email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                />
                { formErrors.contact_information.email && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.contact_information.email}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  id="contactPhone"
                  type="text"
                  name="contactInformation.phone"
                  value={formData.contactInformation.phone}
                  onChange={handleChange}
                  placeholder="Enter Contact Phone"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                />
                {formErrors.contact_information.phone && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.contact_information.phone}
                  </div>
                )}
              </div>
            </div>

            {/* Contact Person */}
            <div>
              <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                id="contactPerson"
                type="text"
                name="contactInformation.contactPerson"
                value={formData.contactInformation.contactPerson}
                onChange={handleChange}
                placeholder="Enter Contact Person"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
              />
              {formErrors.contact_information.contactPerson && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.contact_information.contactPerson}
                </div>
              )}
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">Address</h3>

              {/* Street */}
              <div>
                <label htmlFor="addressStreet" className="block text-sm font-medium text-gray-700 mb-2">
                  Street
                </label>
                <input
                  id="addressStreet"
                  type="text"
                  name="contactInformation.address.street"
                  value={formData.contactInformation.address.street}
                  onChange={handleChange}
                  placeholder="Enter Street"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                />
                {formErrors.contact_information.address.street && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.contact_information.address.street}
                  </div>
                )}
              </div>

              {/* City, State, Country Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="addressCity" className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    id="addressCity"
                    type="text"
                    name="contactInformation.address.city"
                    value={formData.contactInformation.address.city}
                    onChange={handleChange}
                    placeholder="Enter City"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                  {formErrors.contact_information.address.city && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.contact_information.address.city}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="addressState" className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    id="addressState"
                    type="text"
                    name="contactInformation.address.state"
                    value={formData.contactInformation.address.state}
                    onChange={handleChange}
                    placeholder="Enter State"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                  {formErrors.contact_information.address.state && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.contact_information.address.state}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="addressCountry" className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    id="addressCountry"
                    type="text"
                    name="contactInformation.address.country"
                    value={formData.contactInformation.address.country}
                    onChange={handleChange}
                    placeholder="Enter Country"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                  {formErrors.contact_information.address.country && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.contact_information.address.country}
                    </div>
                  )}
                </div>
              </div>

              {/* Zip Code */}
              <div>
                <label htmlFor="addressZipCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Zip Code
                </label>
                <input
                  id="addressZipCode"
                  type="text"
                  name="contactInformation.address.zipCode"
                  value={formData.contactInformation.address.zipCode}
                  onChange={handleChange}
                  placeholder="Enter Zip Code"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                />
                {formErrors.contact_information.address.zipCode && (
                  <div className="text-red-500 text-sm mt-1">
                    {formErrors.contact_information.address.zipCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">
            Settings
          </h2>
          <div className="space-y-6">
            {/* Whitelisted IPs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Whitelisted IPs
              </label>
              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="text"
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                    placeholder="Enter IP address"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddIp}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add IP
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.whiteListedIps.map((ip, index) => (
                    <span key={index} className="bg-gray-100 rounded-full px-3 py-1 text-sm flex items-center gap-2">
                      {ip}
                      <button
                        type="button"
                        onClick={() => handleRemoveIp(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Checkbox */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                type="checkbox"
                name="isNonUniqueReferenceIdAllowed"
                checked={formData.isNonUniqueReferenceIdAllowed}
                onChange={handleChange}
                  className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Allow Non-Unique Reference IDs
                </span>
              </label>
            </div>

            {/* Payment Terms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Distributor Wallet Payment Terms
              </label>
              <div id="paymentType" className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="paymentType"
                    value="0"
                    checked={formData.paymentType === 0}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand border-gray-300 focus:ring-brand focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Prepaid</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="paymentType"
                    value="1"
                    checked={formData.paymentType === 1}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand border-gray-300 focus:ring-brand focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Postpaid</span>
                </label>
              </div>
              {formErrors.paymentType && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.paymentType}
                </div>
              )}
            </div>

            {/* Require Purchase Orders */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isPurchaseOrderRequired"
                  checked={formData.isPurchaseOrderRequired}
                  onChange={handleChange}
                  className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Require Purchase Orders for this Distributor
                </span>
              </label>
            </div>

            {/* Allow Purchase Orders */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="allowPurchaseOrderCreation"
                  checked={formData.allowPurchaseOrderCreation}
                  onChange={handleChange}
                  className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Allow Purchase Orders for this Distributor
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-between pb-8">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </div>
            ) : (
              "Continue"
            )}
          </button>
      </div>
      </form>
    </div>
  );
};

export default AddDistributorPage;
